import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Rocket, ShieldCheck, Zap } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient, getStoredToken } from '@/lib/api';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function WelcomePage() {
  useDocumentTitle('Welcome');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  // Guard so the subscription check fires exactly once regardless of how many
  // times token changes (undefined → value as AuthContext restores from storage).
  const verifyCalledRef = useRef(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (verifyCalledRef.current) return;
    verifyCalledRef.current = true;

    async function verifyCheckout() {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        // GET /v2/billing/subscription to prime KV cache after webhook fires.
        // Prefer the context token; fall back to localStorage so we don't skip
        // the call on the first render when the context token is still undefined.
        const authToken = token || getStoredToken();
        if (authToken) {
          await apiClient.getSubscription(authToken);
        }
      } catch (error) {
        console.error('Error verifying checkout:', error);
        // Still show success — the Stripe webhook will catch up even if the poll fails.
      } finally {
        setLoading(false);
      }
    }

    verifyCheckout();
    // token intentionally omitted: verifyCalledRef prevents double-calls and
    // getStoredToken() provides a synchronous fallback for the initial render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[rgb(5,5,5)] flex flex-col">
        <AppHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
            <p className="text-zinc-400 animate-pulse">Confirming your subscription...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[rgb(5,5,5)] flex flex-col">
      <AppHeader />
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          <Card className="bg-zinc-800/50 border-emerald-500/20 text-center overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-emerald-500 to-sky-500" />
            <CardHeader className="pt-10 pb-6">
              <div className="mx-auto w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
              <CardTitle className="text-3xl font-bold text-white">
                Subscription Confirmed!
              </CardTitle>
              <CardDescription className="text-zinc-400 text-lg mt-2">
                Welcome to the next level of AI security. Your Pro features are now active.
              </CardDescription>
            </CardHeader>
            <CardContent className="px-8 pb-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                <div className="p-4 rounded-xl bg-[rgb(5,5,5)]/50 border border-zinc-700/50">
                  <ShieldCheck className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">
                    Advanced Protection
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-[rgb(5,5,5)]/50 border border-zinc-700/50">
                  <Zap className="w-6 h-6 text-sky-400 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">
                    Unlimited Scans
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-[rgb(5,5,5)]/50 border border-zinc-700/50">
                  <Rocket className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">
                    Priority Analysis
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2 px-8"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  onClick={() => navigate('/account')}
                >
                  View Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-zinc-500 text-sm mt-8">
            A confirmation email and receipt have been sent to your inbox.
          </p>
        </div>
      </main>
    </div>
  );
}
